/**
 * ClientLogoPanel — co-branded deliverables. Upload or link a client logo that
 * appears alongside the Ekselens mark on every generated deliverable (PDF/DOCX/
 * PPTX/XLSX). Locked once the report is finalised.
 */
import React, { useRef, useState } from "react";
import { ImagePlus, Link2, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function ClientLogoPanel({ runId, run, onChange, locked }) {
  const [logoUrl, setLogoUrl] = useState(run?.client_logo_url || "");
  const [urlInput, setUrlInput] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const apply = (url) => { setLogoUrl(url || ""); onChange && onChange(); };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/assessments/runs/${runId}/logo-upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      apply(data.client_logo_url);
      toast.success("Client logo uploaded — it will co-brand every deliverable");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setUrl = async () => {
    if (!urlInput.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/logo`, { client_logo_url: urlInput.trim() });
      apply(data.client_logo_url);
      setUrlInput("");
      toast.success("Client logo set");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set logo");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/assessments/runs/${runId}/logo`, { client_logo_url: "" });
      apply("");
      toast.success("Client logo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove logo");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="client-logo-panel">
      <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em] mb-3">
        <ImagePlus size={14} /> Co-branded outputs · client logo
      </div>

      {logoUrl ? (
        <div className="flex items-center gap-3 mb-3 p-3 bg-zinc-50 border border-zinc-200" data-testid="client-logo-preview">
          <img src={logoUrl} alt="Client logo" className="ek-logo-on-dark h-10 max-w-[160px] object-contain" />
          {!locked && (
            <button type="button" onClick={remove} disabled={busy}
              className="ml-auto text-zinc-400 hover:text-red-600 disabled:opacity-50 inline-flex items-center gap-1 text-[12px]" data-testid="client-logo-remove">
              <Trash2 size={13} /> Remove
            </button>
          )}
        </div>
      ) : (
        <p className="text-[12px] text-zinc-500 mb-3">No client logo set — deliverables show the Ekselens mark only. Add one to co-brand every export.</p>
      )}

      {locked ? (
        <p className="text-[11px] text-amber-700">Report is finalised — re-finalise to change the logo.</p>
      ) : (
        <div className="space-y-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
            onChange={uploadFile} className="hidden" data-testid="client-logo-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
            className="ek-btn ek-btn-outline text-[13px] inline-flex items-center gap-1.5 w-full justify-center disabled:opacity-50" data-testid="client-logo-upload-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Upload logo (PNG/JPG/SVG · max 5 MB)
          </button>
          <div className="flex gap-2">
            <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="…or paste a hosted logo URL"
              className="flex-1 border border-zinc-300 px-3 py-2 text-[13px]" data-testid="client-logo-url-input" />
            <button type="button" onClick={setUrl} disabled={busy || !urlInput.trim()}
              className="ek-btn ek-btn-outline text-[13px] inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="client-logo-url-btn">
              <Link2 size={14} /> Set
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
